import React, { Fragment } from "react";
import styled from "styled-components";
import { compose } from "redux";
import { connect } from "react-redux";
import moment from "moment";
import numberFormatter from "utils/numberFormatter";
import { Divider, Button, Grid } from "@material-ui/core";
import {
  MakeWholeResult,
  YieldMaintenanceTraditionalResult,
  DefeasanceResult,
  YieldMaintenanceAgencyStyleResult,
  AggregateInterestResult,
  PaymentDateOnly,
  SpreadMaintenanceResult
} from "./AnalysisDetailVariants";
import { resetTestData } from "store/state/calculators";
import { Add } from "@material-ui/icons";

const BlueResult = styled.div`
  align-items:center;
  background:#DADCE0;
  color:#000;
  display:flex;
  flex-flow: column nowrap;    
  padding:32px;
  margin-bottom:16px;
`;

const FancyDivider = styled(Divider)`
 &&& {
  background-color: #000;
  height:1px;
  width:100%;
  margin-bottom:4px;
  margin-top:4px;  
 }   
`;
const RightAlignedValue = styled.div`
  && {
    font-family: Work Sans;
    font-style: normal;
    font-weight: 500;
    line-height: 23px;
    letter-spacing: 0em;
    text-transform:uppercase;
    font-size:24px !important;
    text-align:right;
  }
`;
const DateValue = styled.div`
  && {
    font-family: Work Sans;
    font-style: normal;
    font-weight: 500;
    line-height: 23px;
    letter-spacing: 0em;
    text-transform:uppercase;
    font-size: 24px !important;
    text-align:center;
  }
`;
const WhiteBackground = styled.div`    
  text-align:center;  
  background:#fff;  
  width:100%;
  padding:12px 0;
  margin-bottom:8px;
`;
const BigText = styled.div`
  && {
    font-family: Work Sans;
    font-size: 36px !important;
    font-style: normal;
    font-weight: 500;
    line-height: 23px;
    letter-spacing: 0em;    
    margin:8px 0;  
  }
`;
const Label = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: 500;
  line-height: 23px;
  letter-spacing: 0em;
  text-align: left;   
  text-transform:uppercase;
  font-size:13px !important;
`;
const CenterAlignedLabel = styled(Label)`
  && {
    text-align:center;
  }
`;
const RightAlignedLabel = styled(Label)`
  && {  
  text-align:right;
  }
`;
const PrimaryDataWrapper = styled.div`
  display:flex;
  flex-flow:column nowrap;
  width:100%;  
`;
const AddRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
`;
const AddIconWrapper = styled.span`
  font-size:32px;
`;
const VerticalSpace = styled.div`
  margin-top:4px;
  margin-bottom:4px;
`;
const PrepaymentCost = ({ value }) => {
  return (
    <WhiteBackground>
      <BigText>{value}</BigText>
      <CenterAlignedLabel>Prepayment Cost</CenterAlignedLabel>
    </WhiteBackground>
  )
}
const PrimaryData = ({ value, label }) => {
  return (
    <VerticalSpace>
      <RightAlignedValue>{value}</RightAlignedValue>
      <RightAlignedLabel>{label}</RightAlignedLabel>
    </VerticalSpace>
  )
}
const Date = ({ value, label }) => {
  return (
    <VerticalSpace>
      <DateValue>{value}</DateValue>
      <CenterAlignedLabel>{label}</CenterAlignedLabel>
    </VerticalSpace>
  )
}
const Disclosure = styled.div`&& {
  font-family: Work Sans;    
  font-style: normal;
  font-weight: 400;
  line-height: 23px;
  letter-spacing: 0em;  
  font-size:13px !important;
}
`;
const AdditionalInfoButton = styled(Button)`
  && {
    font-family: Work Sans;
    font-style: normal;
    font-weight: 500;
    line-height: 23px;
    letter-spacing: 0em;
    padding-left:0;
    align-self:flex-start;    
  }
  &&:hover {
    background:none;    
  }
`;
const AdditionalInformationWrapper = styled.div`
  
  && {
    text-align:left; 
    width: 100%;
  }
`
const FlexContainer = styled.div`
  display:flex;
  flex-flow:column nowrap;
  justify-content:center;
  align-items:center;
`;

class AnalysisResult extends React.Component {
  state = {
    isAdditionalInfoOpen: false,
    additionalInfoButtonText: 'show additional information'
  }

  componentDidMount() {
    if (this.props.prepaymentResult != {}) {
      this.props.resetTestData();
    }
  }

  toggleAdditionalInfo = () => {
    if (this.state.isAdditionalInfoOpen) {
      this.setState({
        isAdditionalInfoOpen: false,
        additionalInfoButtonText: 'show additional information'
      })
    } else {
      this.setState({
        isAdditionalInfoOpen: true,
        additionalInfoButtonText: 'hide additional information'
      })
    }
  }

  render() {
    const { prepaymentResult } = this.props;
    const {
      prepaymentPenalty,
      prepaymentDate,
      accruedInterest,
      balanceAtPrepayment,
      totalDueToLender,
      makeWholeResult,
      yieldMaintenanceResult,
      yieldMaintenanceAgencyStyleResult,
      defeasanceResult,
      aggregateInterestResult,
      spreadMaintenanceResult,
      flatPercentageSchedule,
      exitFee
    } = prepaymentResult
    if (!totalDueToLender)
      return null;
    const shouldIncludeAccruedInterest = true; // Added per US10409
    const shouldUsePV = spreadMaintenanceResult && this.props.prepaymentParameters?.prepaymentParameters?.shouldUsePV;
    return (
      <Fragment>
        <BlueResult>
          {<PrepaymentCost value={numberFormatter.formatMoney(prepaymentPenalty, 2)} />}
          <Date value={moment(prepaymentDate).format('MM/DD/YYYY')} label='Prepayment Date' />
          <PrimaryDataWrapper>
            <PrimaryData value={numberFormatter.formatMoney(prepaymentPenalty, 2)} label='Prepayment Penalty' />
            {!!balanceAtPrepayment && <PrimaryData value={numberFormatter.formatMoney(balanceAtPrepayment, 2)} label='Balance at prepayment' />}
            {!!accruedInterest && <PrimaryData value={numberFormatter.formatMoney(accruedInterest, 2)} label='Accrued Interest' />}
            {
              !!defeasanceResult &&
              !!defeasanceResult.totalThirdPartyFees &&
              <PrimaryData value={numberFormatter.formatMoney(defeasanceResult.totalThirdPartyFees, 2)} label='Transaction Costs' />
            }
            <AddRow>
              <AddIconWrapper><Add fontSize='inherit' /></AddIconWrapper>
              {!!exitFee && <PrimaryData value={numberFormatter.formatMoney(exitFee, 2)} label='Exit Fees' />}
            </AddRow>
            <FancyDivider />
            <PrimaryData value={numberFormatter.formatMoney(totalDueToLender, 2)} label='total due to lender' />
          </PrimaryDataWrapper>
          <AdditionalInfoButton onClick={this.toggleAdditionalInfo}>
            {this.state.additionalInfoButtonText}
          </AdditionalInfoButton>
          {this.state.isAdditionalInfoOpen && (
            <AdditionalInformationWrapper>
              <FlexContainer>
                <Grid container justify="space-between">
                  {makeWholeResult && <MakeWholeResult result={makeWholeResult} />}
                  {yieldMaintenanceResult && <YieldMaintenanceTraditionalResult result={yieldMaintenanceResult} />}
                  {yieldMaintenanceAgencyStyleResult && <YieldMaintenanceAgencyStyleResult result={yieldMaintenanceAgencyStyleResult} />}
                  {defeasanceResult && <DefeasanceResult result={defeasanceResult} />}
                  {aggregateInterestResult && <AggregateInterestResult result={aggregateInterestResult} />}
                  {spreadMaintenanceResult && <SpreadMaintenanceResult result={spreadMaintenanceResult} shouldUsePV={shouldUsePV} />}
                  {flatPercentageSchedule && <PaymentDateOnly result={prepaymentResult} />}
                </Grid>
              </FlexContainer>
            </AdditionalInformationWrapper>)}
        </BlueResult>
        <Disclosure>Calculations are for informational purposes and subject to change with market conditions. These calculations are based on the next payment date.</Disclosure>
      </Fragment>
    )
  };

}
function mapStateToProps({ testHarness }) {
  return {
    prepaymentResult: testHarness.prepaymentPenalty
  }
}
export default compose(
  connect(
    mapStateToProps,
    { resetTestData }
  )
)(AnalysisResult);